import { useState, useEffect } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { ArrowLeft, Camera, DollarSign, Save, Copy, AlertTriangle, Check, FileDown, PenLine, ClipboardList, Share2, Stethoscope, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog"
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { supabase } from "@/integrations/supabase/client"
import type { TablesInsert } from "@/integrations/supabase/types"
import { useAuth } from "@/contexts/AuthContext"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { normalizarTelefoneWhatsApp } from "@/lib/oficinaAtendimento"
import { getRiscoCompra } from "@/lib/lojaComercial"
import { toast } from "sonner"
import { buscarUltimoRegistroFIPE, salvarHistoricoFIPE, type OrigemFIPE } from "@/lib/fipeService"
import { aplicarMascaraMoeda, parsearMoeda, formatarMoedaInput } from "@/lib/utils"
import { criteriosData } from "@/lib/criteriosData"
import { createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import { pdf } from '@react-pdf/renderer'
import PropostaPDF from "@/components/PropostaPDF"
import { SignaturePad } from "@/components/SignaturePad"

interface Veiculo {
  id: string
  placa: string
  marca: string
  modelo: string
  ano: number
  anomodelo?: string | null
  cor?: string | null
  combustivel?: string | null
  cambio?: string | null
  quilometragem?: number | null
  motor?: string | null
  portas?: number | null
  foto_principal?: string | null
}

interface AvaliacaoCriterio {
  criterio_id: string
  aprovado: boolean | null
  status?: string | null
  observacoes: string | null
  custo_reparo: number | null
  nome_criterio?: string
  categoria_criterio?: string
}

interface FotoReparo {
  categoria: string
  url: string
}

interface AvaliacaoDetalhe {
  id: string
  nota_final?: number | null
  objetivo_comercial?: string | null
  observacoes_iniciais?: string | null
  share_token?: string | null
  cliente_nome?: string | null
  cliente_telefone?: string | null
  cliente_assinatura?: string | null
  cliente_assinado_em?: string | null
  veiculos: Veiculo
}

const categoriaPorPrefixo: Record<string, string> = {
  frontal: "Frontal",
  traseira: "Traseira",
  latdir: "Lateral Direita",
  latesq: "Lateral Esquerda",
  motor: "Motor",
  painel: "Painel e Instrumentos",
  interior: "Interior",
  extras: "Detalhes Extras"
}

const normalizarCriterioId = (id: string) => id.trim().toLowerCase()

const obterCategoria = (criterio: AvaliacaoCriterio) => {
  if (criterio.categoria_criterio) return criterio.categoria_criterio
  const prefixo = normalizarCriterioId(criterio.criterio_id).split("_")[0]
  return categoriaPorPrefixo[prefixo] || ""
}

const obterNome = (criterio: AvaliacaoCriterio) => {
  if (criterio.nome_criterio) return criterio.nome_criterio
  const criterioLocal = criteriosData.find(c => c.id === normalizarCriterioId(criterio.criterio_id))
  return criterioLocal?.nome || criterio.criterio_id
}

const isCriterioReprovado = (criterio: AvaliacaoCriterio) =>
  criterio.status ? criterio.status === "reprovado" : criterio.aprovado === false

const MARGEM_FECHAMENTO_PADRAO = 0
type ModalidadeDocumentoLoja = 'compra' | 'consignacao'

const LABEL_CATEGORIA_FOTO: Record<string, string> = {
  "Frontal": "Frente",
  "Traseira": "Traseira",
  "Lateral Direita": "Lat. Direita",
  "Lateral Esquerda": "Lat. Esquerda",
  "Painel e Instrumentos": "Painel / km",
  "Interior": "Interior",
  "Motor": "Motor",
  "Detalhes Extras": "Extras",
}

const CONSIGNACAO_MARKER = '[weauto:termo-consignacao]'

const montarObservacoesConsignacao = (dados: {
  valorAnunciado: string
  comissaoLoja: string
  prazoConsignacao: string
  condicoesConsignacao: string
  observacoesAvaliacao?: string | null
}) => {
  const linhas = [
    CONSIGNACAO_MARKER,
    `valor_anunciado=${dados.valorAnunciado}`,
    `comissao_loja=${dados.comissaoLoja}`,
    `prazo_consignacao=${dados.prazoConsignacao}`,
    `condicoes=${dados.condicoesConsignacao.replace(/\n/g, '\\n')}`,
  ]

  if (dados.observacoesAvaliacao) {
    linhas.push(`observacoes_avaliacao=${dados.observacoesAvaliacao.replace(/\n/g, '\\n')}`)
  }

  return linhas.join('\n')
}

const parseObservacoesConsignacao = (observacoes?: string | null) => {
  if (!observacoes?.includes(CONSIGNACAO_MARKER)) return null

  const getValue = (key: string) => {
    const line = observacoes.split('\n').find((item) => item.startsWith(`${key}=`))
    return line?.replace(`${key}=`, '').replace(/\\n/g, '\n') ?? ''
  }

  return {
    valorAnunciado: getValue('valor_anunciado'),
    comissaoLoja: getValue('comissao_loja'),
    prazoConsignacao: getValue('prazo_consignacao'),
    condicoesConsignacao: getValue('condicoes'),
    observacoesAvaliacao: getValue('observacoes_avaliacao'),
  }
}

const PropostaDetalhes = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { loja } = useAuth()
  const vertical = useTipoOperacao()
  const mostrarFinanceiro = vertical.documentos.mostrarValoresFinanceiros
  const [avaliacao, setAvaliacao] = useState<AvaliacaoDetalhe | null>(null)
  const [veiculo, setVeiculo] = useState<Veiculo | null>(null)
  const [criterios, setCriterios] = useState<AvaliacaoCriterio[]>([])
  const [fotosReparos, setFotosReparos] = useState<FotoReparo[]>([])
  const [fotosGerais, setFotosGerais] = useState<Array<{ url: string; categoria: string }>>([])
  const [valorFIPE, setValorFIPE] = useState<number>(0)
  const [valorFIPEInput, setValorFIPEInput] = useState("")
  const [valorFIPEOrigem, setValorFIPEOrigem] = useState<OrigemFIPE | null>(null)
  const [valorFIPEInicial, setValorFIPEInicial] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [oferta, setOferta] = useState("")
  const margemLucro = MARGEM_FECHAMENTO_PADRAO
  const [carregandoFIPE, setCarregandoFIPE] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [propostaSalva, setPropostaSalva] = useState(false)
  const [gerandoPDF, setGerandoPDF] = useState(false)
  const [incluirAssinatura, setIncluirAssinatura] = useState(false)
  const [assinaturaNome, setAssinaturaNome] = useState("")
  const [assinaturaDocumento, setAssinaturaDocumento] = useState("")
  const [assinaturaTelefone, setAssinaturaTelefone] = useState("")
  const [assinaturaDataUrl, setAssinaturaDataUrl] = useState("")
  const [shareToken, setShareToken] = useState<string | null>(null)
  const [propostaId, setPropostaId] = useState<string | null>(null)
  const [propostaShareToken, setPropostaShareToken] = useState<string | null>(null)
  const [compartilhando, setCompartilhando] = useState(false)
  const [modalidadeDocumento, setModalidadeDocumento] = useState<ModalidadeDocumentoLoja>('compra')
  const [valorAnunciado, setValorAnunciado] = useState("")
  const [comissaoLoja, setComissaoLoja] = useState("")
  const [prazoConsignacao, setPrazoConsignacao] = useState("")
  const [condicoesConsignacao, setCondicoesConsignacao] = useState("")

  useEffect(() => {
    if (id) fetchDadosProposta()
  }, [id])

  const fetchDadosProposta = async () => {
    if (!id) return

    let custoTotalInicial = 0
    try {
      const { data: avaliacaoData, error: avaliacaoError } = await supabase
        .from('avaliacoes')
        .select(`*, veiculos (*)`)
        .eq('id', id)
        .single()

      if (avaliacaoError) {
        toast.error("Erro ao carregar avaliação")
        return
      }

      const fotoPrincipalUrl = await createSignedFileUrl(PHOTO_BUCKET, avaliacaoData.veiculos?.foto_principal)
      const veiculoComFoto = {
        ...avaliacaoData.veiculos,
        foto_principal: fotoPrincipalUrl ?? avaliacaoData.veiculos?.foto_principal,
      }

      setAvaliacao({
        ...avaliacaoData,
        veiculos: veiculoComFoto,
      })
      setVeiculo(veiculoComFoto)
      setShareToken(avaliacaoData.share_token ?? null)

      if (avaliacaoData.cliente_assinado_em) {
        setIncluirAssinatura(true)
        setAssinaturaNome(avaliacaoData.cliente_nome ?? "")
        setAssinaturaTelefone(avaliacaoData.cliente_telefone ?? "")
        setAssinaturaDataUrl(avaliacaoData.cliente_assinatura ?? "")
      } else if (!mostrarFinanceiro) {
        setAssinaturaNome(avaliacaoData.cliente_nome ?? "")
        setAssinaturaTelefone(avaliacaoData.cliente_telefone ?? "")
      }

      const { data: criteriosDoBanco, error: criteriosError } = await supabase
        .from('avaliacoes_criterios')
        .select('*')
        .eq('avaliacao_id', id)

      if (criteriosError) {
        console.error("Erro ao buscar critérios:", criteriosError)
      } else {
        const criteriosCompletos = (criteriosDoBanco || []).map(item => {
          const idNorm = normalizarCriterioId(item.criterio_id)
          const local = criteriosData.find(c => c.id === idNorm)
          return {
            ...item,
            nome_criterio: local?.nome || '',
            categoria_criterio: local?.categoria || categoriaPorPrefixo[idNorm.split("_")[0]] || ''
          }
        })
        setCriterios(criteriosCompletos)
        custoTotalInicial = criteriosCompletos
          .filter((c) => isCriterioReprovado(c))
          .reduce((total, c) => total + (c.custo_reparo || 0), 0)
      }

      const { data: fotosReparosData } = await supabase
        .from('fotos_veiculo')
        .select('categoria, url')
        .eq('avaliacao_id', id)
        .or('categoria.like.criterio_reprovado_%,categoria.like.criterio_atencao_%')

      const fotosReparosResolvidas = await Promise.all(
        (fotosReparosData || []).map(async (foto) => ({
          ...foto,
          url: (await createSignedFileUrl(PHOTO_BUCKET, foto.url)) ?? foto.url,
        }))
      )

      setFotosReparos(fotosReparosResolvidas)

      const { data: fotosGeraisData } = await supabase
        .from('fotos_veiculo')
        .select('categoria, url')
        .eq('avaliacao_id', id)
        .ilike('descricao', '%principal%')

      const fotosGeraisResolvidas = await Promise.all(
        (fotosGeraisData || []).map(async (foto) => ({
          categoria: foto.categoria,
          url: (await createSignedFileUrl(PHOTO_BUCKET, foto.url)) ?? foto.url,
        }))
      )
      setFotosGerais(fotosGeraisResolvidas)

      const { data: propostaExistente } = await supabase
        .from('propostas')
        .select('id, observacoes, valor_ofertado, modalidade, share_token, cliente_nome, cliente_telefone')
        .eq('avaliacao_id', id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      const dadosConsignacao = parseObservacoesConsignacao(propostaExistente?.observacoes)
      setPropostaId(propostaExistente?.id ?? null)
      setPropostaShareToken(propostaExistente?.share_token ?? null)
      if (mostrarFinanceiro && propostaExistente) {
        setAssinaturaNome(propostaExistente.cliente_nome ?? assinaturaNome)
        setAssinaturaTelefone(propostaExistente.cliente_telefone ?? assinaturaTelefone)
      }
      const modalidadePersistida = propostaExistente?.modalidade === 'consignacao' ? 'consignacao' : 'compra'
      if (mostrarFinanceiro && propostaExistente) {
        setModalidadeDocumento(dadosConsignacao ? 'consignacao' : modalidadePersistida)
      } else if (mostrarFinanceiro && avaliacaoData.objetivo_comercial === "consignacao") {
        setModalidadeDocumento("consignacao")
      }

      if (mostrarFinanceiro && dadosConsignacao) {
        setModalidadeDocumento('consignacao')
        setValorAnunciado(dadosConsignacao.valorAnunciado || formatarMoedaInput(propostaExistente?.valor_ofertado ?? 0))
        setComissaoLoja(dadosConsignacao.comissaoLoja)
        setPrazoConsignacao(dadosConsignacao.prazoConsignacao)
        setCondicoesConsignacao(dadosConsignacao.condicoesConsignacao)
      }

      if (mostrarFinanceiro) {
        await consultarFIPE(avaliacaoData.veiculos.id, custoTotalInicial)
      } else {
        setCarregandoFIPE(false)
      }

    } catch (error) {
      toast.error("Erro inesperado ao carregar dados")
      console.error("Erro:", error)
    } finally {
      setLoading(false)
    }
  }

  const consultarFIPE = async (veiculoId: string, custoTotal: number) => {
    setCarregandoFIPE(true)
    try {
      const ultimoFIPE = await buscarUltimoRegistroFIPE(veiculoId)
      if (ultimoFIPE !== null) {
        setValorFIPE(ultimoFIPE.valor_fipe)
        setValorFIPEInicial(ultimoFIPE.valor_fipe)
        setValorFIPEInput(formatarMoedaInput(ultimoFIPE.valor_fipe))
        setValorFIPEOrigem(ultimoFIPE.origem)
        setOferta(formatarMoedaInput(Math.max(0, ultimoFIPE.valor_fipe - custoTotal)))
      } else {
        setValorFIPE(0)
        setValorFIPEInicial(null)
        setValorFIPEInput("")
        setValorFIPEOrigem(null)
        toast.info("Valor FIPE não encontrado. Informe manualmente para calcular o fechamento.")
      }
    } catch (error) {
      console.error("Erro ao buscar FIPE:", error)
      toast.error("Erro ao buscar valor FIPE")
    } finally {
      setCarregandoFIPE(false)
    }
  }

  const calcularCustoTotal = () =>
    criterios.filter((c) => isCriterioReprovado(c)).reduce((t, c) => t + (c.custo_reparo || 0), 0)

  const calcularValorSugerido = () => Math.max(0, valorFIPE - calcularCustoTotal())

  const calcularPorcentagemFipe = () => {
    const valorOfertado = parsearMoeda(oferta)
    if (!valorFIPE || !valorOfertado) return null
    const pct = (valorOfertado - valorFIPE) / valorFIPE * 100
    if (Math.abs(pct) < 0.05) return null
    return pct.toFixed(1)
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

  const handleValorFIPEChange = (raw: string) => {
    const valor = parsearMoeda(raw)
    setValorFIPEInput(raw)
    setValorFIPE(valor)
    setValorFIPEOrigem(valor > 0 ? 'manual' : null)
    setOferta(valor > 0 ? formatarMoedaInput(Math.max(0, valor - calcularCustoTotal())) : "")
  }

  const handleDeletarProposta = async () => {
    if (!propostaId || !id) return
    try {
      const { error: deleteError } = await supabase
        .from("propostas")
        .delete()
        .eq("id", propostaId)
      if (deleteError) { toast.error("Erro ao excluir o documento"); return }

      const { error: resetError } = await supabase
        .from("avaliacoes")
        .update({ share_token: null, cliente_assinado_em: null })
        .eq("id", id)
      if (resetError) { toast.error("Erro ao limpar dados do atendimento"); return }

      toast.success("Documento excluído")
      navigate(veiculo?.id ? `/veiculo/${veiculo.id}` : "/avaliacoes")
    } catch {
      toast.error("Erro inesperado ao excluir documento")
    }
  }

  const handleGerarPDF = async () => {
    if (!veiculo) return

    if (isConsignacao) {
      if (!Number.isFinite(parsearMoeda(valorAnunciado)) || parsearMoeda(valorAnunciado) <= 0) {
        toast.error("Informe um valor anunciado válido")
        return
      }
      if (!comissaoLoja.trim()) {
        toast.error("Informe a comissão da loja")
        return
      }
      if (!prazoConsignacao.trim()) {
        toast.error("Informe o prazo de consignação")
        return
      }
    }

    if (incluirAssinatura) {
      if (!assinaturaNome.trim()) {
        toast.error("Informe o nome de quem vai assinar.")
        return
      }
      if (mostrarFinanceiro && !assinaturaDocumento.trim()) {
        toast.error("Informe o CPF ou CNPJ de quem vai assinar.")
        return
      }
      if (mostrarFinanceiro && !assinaturaTelefone.trim()) {
        toast.error("Informe o telefone de quem vai assinar.")
        return
      }
      if (!assinaturaDataUrl) {
        toast.error("Colete a assinatura antes de gerar o documento final.")
        return
      }
    }

    setGerandoPDF(true)
    try {
      const reparosParaPDF = reparos.map(criterio => ({
        nome: obterNome(criterio),
        categoria: obterCategoria(criterio),
        custo: criterio.custo_reparo || 0,
        observacoes: criterio.observacoes || undefined,
        fotoUrl: fotosReparos.find(
          f => f.categoria === `criterio_reprovado_${criterio.criterio_id}`
        )?.url,
      }))

      const blob = await pdf(
        <PropostaPDF
          modoOficina={!mostrarFinanceiro}
          documentoTipo={isConsignacao ? 'consignacao' : mostrarFinanceiro ? 'compra' : 'oficina'}
          consignacao={
            isConsignacao
              ? {
                  valorAnunciado: parsearMoeda(valorAnunciado),
                  comissaoLoja: comissaoLoja.trim(),
                  prazoConsignacao: prazoConsignacao.trim(),
                  condicoes: condicoesConsignacao.trim(),
                }
              : undefined
          }
          placa={veiculo.placa}
          marca={veiculo.marca}
          modelo={veiculo.modelo}
          ano={veiculo.ano}
          anoModelo={veiculo.anomodelo ?? undefined}
          cor={veiculo.cor ?? undefined}
          combustivel={veiculo.combustivel ?? undefined}
          cambio={veiculo.cambio ?? undefined}
          quilometragem={veiculo.quilometragem ?? undefined}
          motor={veiculo.motor ?? undefined}
          portas={veiculo.portas ?? undefined}
          fotoPrincipalUrl={veiculo.foto_principal ?? undefined}
          notaFinal={avaliacao?.nota_final ?? undefined}
          valorFIPE={valorFIPE}
          custoReparos={custoTotal}
          valorLiquido={valorSugerido}
          margemLucro={margemLucro}
          valorOfertado={isConsignacao ? parsearMoeda(valorAnunciado) : parsearMoeda(oferta)}
          reparos={reparosParaPDF}
          observacoes={avaliacao?.observacoes_iniciais || undefined}
          assinatura={
            incluirAssinatura
              ? {
                  nome: assinaturaNome.trim(),
                  documento: mostrarFinanceiro ? assinaturaDocumento.trim() : undefined,
                  telefone: assinaturaTelefone.trim() || undefined,
                  imagemUrl: assinaturaDataUrl,
                  emitidaEm: new Date().toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                }
              : undefined
          }
        />
      ).toBlob()

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const slug = isConsignacao ? 'termo-consignacao' : mostrarFinanceiro ? 'proposta-compra' : 'termo-entrada'
      link.download = `${slug}-${veiculo.placa}.pdf`
      link.click()
      URL.revokeObjectURL(url)
      toast.success(`${tituloDocumento} gerado com sucesso!`)
    } catch (err) {
      console.error("Erro ao gerar PDF:", err)
      toast.error(`Erro ao gerar ${tituloDocumento.toLowerCase()}`)
    } finally {
      setGerandoPDF(false)
    }
  }

  const handleCopiarResumo = () => {
    if (!veiculo) return
    const custoTotal = calcularCustoTotal()
    const reparos = criterios.filter((c) => isCriterioReprovado(c))
    const itensAtencao = criterios.filter((c) => c.status === "atencao")

    const linhasReparosFinanceiro = reparos
      .map(c => `• ${obterNome(c)} (${obterCategoria(c)}): ${formatCurrency(c.custo_reparo || 0)}`)
      .join('\n')

    const linhasReparosSemValor = reparos
      .map(c => `• ${obterNome(c)} (${obterCategoria(c)})`)
      .join('\n')

    const pct = calcularPorcentagemFipe()
    const pctTexto = pct ? ` (${Number(pct) > 0 ? '+' : ''}${pct}% do FIPE)` : ''

    const linhasFinanceiras = isConsignacao
      ? [
          `Valor anunciado: ${formatCurrency(parsearMoeda(valorAnunciado))}`,
          `Comissão da loja: ${comissaoLoja || 'Não informada'}`,
          `Prazo de consignação: ${prazoConsignacao || 'Não informado'}`,
          ...(condicoesConsignacao ? [`Condições: ${condicoesConsignacao}`] : []),
        ]
      : mostrarFinanceiro
        ? [
          `FIPE: ${formatCurrency(valorFIPE)}`,
          `Reparos: ${formatCurrency(custoTotal)} (${reparos.length} item${reparos.length !== 1 ? 's' : ''})`,
          `Valor Líquido: ${formatCurrency(calcularValorSugerido())}`,
          `Oferta: ${oferta}${pctTexto}`,
        ]
        : [
          `Partes vistoriadas: ${criterios.length}`,
          `Com observação: ${itensAtencao.length}`,
          `Com dano: ${reparos.length}`,
        ]

    const linhasReparos = mostrarFinanceiro && !isConsignacao ? linhasReparosFinanceiro : linhasReparosSemValor
    const labelSecaoReparos = isConsignacao ? 'Pontos documentados na avaliação:' : mostrarFinanceiro ? 'Reparos necessários:' : 'Danos registrados:'

    const texto = [
      `${tituloDocumento} — ${veiculo.placa}`,
      `${veiculo.marca} ${veiculo.modelo} ${veiculo.ano}`,
      '',
      ...linhasFinanceiras,
      ...(reparos.length > 0 ? ['', labelSecaoReparos, linhasReparos] : []),
      ...(avaliacao?.observacoes_iniciais ? ['', `Observações: ${avaliacao.observacoes_iniciais}`] : []),
    ].join('\n')

    navigator.clipboard.writeText(texto)
      .then(() => toast.success("Resumo copiado para a área de transferência"))
      .catch(() => toast.error("Erro ao copiar resumo"))
  }

  const salvarProposta = async (): Promise<{ id: string; share_token: string | null } | null> => {
    const custoTotal = calcularCustoTotal()

    if (!id || !avaliacao) {
      toast.error("Dados da avaliação não encontrados")
      return null
    }

    const valorOfertado = mostrarFinanceiro
      ? (modalidadeDocumento === 'consignacao' ? parsearMoeda(valorAnunciado) : parsearMoeda(oferta))
      : 0
    if (mostrarFinanceiro && modalidadeDocumento === 'compra') {
      if (!Number.isFinite(valorOfertado) || valorOfertado <= 0) {
        toast.error("Informe um valor válido para a oferta")
        return null
      }
      if (!Number.isFinite(valorFIPE) || valorFIPE <= 0) {
        toast.error("Informe um valor FIPE válido para salvar o fechamento")
        return null
      }
    }
    if (isConsignacao) {
      if (!Number.isFinite(valorOfertado) || valorOfertado <= 0) {
        toast.error("Informe um valor anunciado válido")
        return null
      }
      if (!comissaoLoja.trim()) {
        toast.error("Informe a comissão da loja")
        return null
      }
      if (!prazoConsignacao.trim()) {
        toast.error("Informe o prazo de consignação")
        return null
      }
    }

    setSalvando(true)

    try {
      const fipeManualAlterada = valorFIPEInicial === null || Math.abs(valorFIPE - valorFIPEInicial) >= 0.01
      if (mostrarFinanceiro && modalidadeDocumento === 'compra' && valorFIPEOrigem === 'manual' && fipeManualAlterada && veiculo) {
        await salvarHistoricoFIPE(veiculo.id, valorFIPE, veiculo.placa, 'manual')
      }

      const observacoesPayload = isConsignacao
        ? montarObservacoesConsignacao({
            valorAnunciado,
            comissaoLoja: comissaoLoja.trim(),
            prazoConsignacao: prazoConsignacao.trim(),
            condicoesConsignacao: condicoesConsignacao.trim(),
            observacoesAvaliacao: avaliacao.observacoes_iniciais,
          })
        : avaliacao.observacoes_iniciais || ''

      const payload = {
        valor_fipe: mostrarFinanceiro && modalidadeDocumento === 'compra' ? valorFIPE : 0,
        custo_reparos: mostrarFinanceiro && modalidadeDocumento === 'compra' ? custoTotal : 0,
        valor_liquido_sugerido: mostrarFinanceiro && modalidadeDocumento === 'compra' ? Math.max(0, valorFIPE - custoTotal) : 0,
        margem_lucro: mostrarFinanceiro && modalidadeDocumento === 'compra' ? margemLucro : 0,
        valor_ofertado: valorOfertado,
        modalidade: mostrarFinanceiro ? modalidadeDocumento : 'compra',
        observacoes: observacoesPayload
      }

      const updateResponse = await supabase
        .from('propostas')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('avaliacao_id', id)
        .select('id, share_token')

      const resultado =
        updateResponse.data && updateResponse.data.length > 0
          ? updateResponse
          : await supabase
              .from('propostas')
              .insert({ loja_id: loja?.id ?? '', avaliacao_id: id, ...payload, status: 'pendente' } satisfies TablesInsert<'propostas'>)
              .select()
              .single()

      if (resultado.error) throw resultado.error

      const propostaSalvaAtual = Array.isArray(resultado.data)
        ? resultado.data[0]
        : resultado.data

      if (propostaSalvaAtual?.id) {
        setPropostaId(propostaSalvaAtual.id)
        setPropostaShareToken(propostaSalvaAtual.share_token ?? null)
      }

      if (incluirAssinatura && assinaturaDataUrl && assinaturaNome.trim()) {
        if (mostrarFinanceiro && propostaSalvaAtual?.id) {
          await supabase
            .from('propostas')
            .update({
              cliente_nome: assinaturaNome.trim(),
              cliente_documento: assinaturaDocumento.trim() || null,
              cliente_telefone: assinaturaTelefone.trim() || null,
              cliente_assinatura: assinaturaDataUrl,
              cliente_assinado_em: new Date().toISOString(),
            })
            .eq('id', propostaSalvaAtual.id)
        }
        await supabase
          .from('avaliacoes')
          .update({
            cliente_nome: assinaturaNome.trim(),
            cliente_telefone: assinaturaTelefone.trim() || null,
            cliente_assinatura: assinaturaDataUrl,
            cliente_assinado_em: new Date().toISOString(),
          })
          .eq('id', id)
      } else if (mostrarFinanceiro && propostaSalvaAtual?.id && (assinaturaNome.trim() || assinaturaTelefone.trim())) {
        await supabase
          .from('propostas')
          .update({
            cliente_nome: assinaturaNome.trim() || null,
            cliente_telefone: assinaturaTelefone.trim() || null,
          })
          .eq('id', propostaSalvaAtual.id)
      } else if (!mostrarFinanceiro && (assinaturaNome.trim() || assinaturaTelefone.trim())) {
        await supabase
          .from('avaliacoes')
          .update({
            cliente_nome: assinaturaNome.trim() || null,
            cliente_telefone: assinaturaTelefone.trim() || null,
          })
          .eq('id', id)
      }

      setPropostaSalva(true)
      toast.success(`${tituloDocumento} salvo com sucesso!`, {
        action: { label: "Gerar PDF", onClick: () => void handleGerarPDF() }
      })
      return propostaSalvaAtual?.id
        ? { id: propostaSalvaAtual.id, share_token: propostaSalvaAtual.share_token ?? null }
        : null
    } catch (error) {
      console.error(`Erro ao salvar ${tituloDocumento.toLowerCase()}:`, error)
      toast.error(`Erro ao salvar ${tituloDocumento.toLowerCase()}`)
      return null
    } finally {
      setSalvando(false)
    }
  }

  const handleCompartilhar = async () => {
    if (!id || !veiculo) return
    setCompartilhando(true)
    try {
      const token = shareToken ?? crypto.randomUUID()

      if (!shareToken) {
        const { error } = await supabase
          .from('avaliacoes')
          .update({ share_token: token })
          .eq('id', id)
        if (error) throw error
        setShareToken(token)
      }

      const url = `${window.location.origin}/t/${token}`
      const texto = `Olá${assinaturaNome ? `, ${assinaturaNome}` : ""}! Segue o termo de entrada do veículo *${veiculo.placa}* (${veiculo.marca} ${veiculo.modelo} ${veiculo.ano}) para conferência e ciência:\n${url}`
      const telefone = normalizarTelefoneWhatsApp(assinaturaTelefone)

      window.open(`https://wa.me/${telefone}?text=${encodeURIComponent(texto)}`, '_blank')
    } catch {
      toast.error('Erro ao gerar link de compartilhamento')
    } finally {
      setCompartilhando(false)
    }
  }

  const copiarLinkProposta = (token: string) =>
    navigator.clipboard.writeText(`${window.location.origin}/p/${token}`)
      .then(() => toast.success("Link copiado"))
      .catch(() => toast.error("Erro ao copiar link"))

  const handleCompartilharProposta = async () => {
    if (!veiculo) return
    setCompartilhando(true)
    try {
      let idProposta = propostaId
      let token = propostaShareToken

      if (!idProposta) {
        const propostaSalvaAtual = await salvarProposta()
        if (!propostaSalvaAtual) return
        idProposta = propostaSalvaAtual.id
        token = propostaSalvaAtual.share_token
      }

      if (!token) {
        token = crypto.randomUUID()
        const { error } = await supabase
          .from('propostas')
          .update({ share_token: token })
          .eq('id', idProposta)
        if (error) throw error
        setPropostaShareToken(token)
      }

      const url = `${window.location.origin}/p/${token}`
      const tipoDocumento = isConsignacao ? 'termo de consignação' : 'proposta de compra'
      const texto = `Olá! Segue ${tipoDocumento} do veículo *${veiculo.placa}* (${veiculo.marca} ${veiculo.modelo} ${veiculo.ano}) pela ${loja?.nome ?? 'loja'}.\n\nAcesse o link:\n${url}`
      const telefone = normalizarTelefoneWhatsApp(assinaturaTelefone)

      await copiarLinkProposta(token)
      window.open(`https://wa.me/${telefone}?text=${encodeURIComponent(texto)}`, '_blank')
    } catch (error) {
      console.error("Erro ao gerar link da proposta:", error)
      toast.error("Erro ao gerar link de compartilhamento")
    } finally {
      setCompartilhando(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (!veiculo) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Veículo não encontrado</p>
        <Button asChild className="mt-4">
          <Link to="/avaliacoes">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Avaliações
          </Link>
        </Button>
      </div>
    )
  }

  const custoTotal = calcularCustoTotal()
  const valorSugerido = calcularValorSugerido()
  const riscoCompra = getRiscoCompra(valorFIPE, custoTotal)
  const porcentagemFipe = calcularPorcentagemFipe()
  const reparos = criterios
    .filter((c) => isCriterioReprovado(c))
    .sort((left, right) => (right.custo_reparo || 0) - (left.custo_reparo || 0))

  const atencaoItems = criterios.filter((c) => c.status === "atencao")
  const isLoja = mostrarFinanceiro
  const isConsignacao = isLoja && modalidadeDocumento === 'consignacao'
  const tituloDocumento = !isLoja
    ? vertical.documentos.tituloPagina
    : isConsignacao
      ? 'Termo de Consignação'
      : 'Proposta de Compra'
  const descricaoDocumento = !isLoja
    ? vertical.documentos.descricaoPagina
    : isConsignacao
      ? 'Defina as condições de consignação e gere o termo para ciência do proprietário'
      : 'Revise a avaliação e gere a proposta comercial de compra'
  const tituloCardResumo = !isLoja
    ? 'Resumo da Entrada'
    : isConsignacao
      ? 'Termo de Consignação'
      : 'Resumo da Proposta'
  const ctaSalvarDocumento = !isLoja
    ? vertical.documentos.ctaSalvar
    : isConsignacao
      ? 'Salvar termo'
      : 'Salvar proposta'
  const ctaSalvoDocumento = !isLoja
    ? vertical.documentos.ctaSalvo
    : isConsignacao
      ? 'Termo salvo'
      : 'Proposta salva'
  const ctaGerarDocumento = !isLoja
    ? vertical.documentos.ctaGerar
    : isConsignacao
      ? 'Gerar termo de consignação'
      : 'Gerar proposta de compra'
  const ctaGerandoDocumento = !isLoja
    ? vertical.documentos.ctaGerando
    : isConsignacao
      ? 'Gerando termo...'
      : 'Gerando proposta...'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/avaliacoes">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex items-center gap-3 flex-1">
          {veiculo.foto_principal && (
            <img
              src={veiculo.foto_principal}
              alt={veiculo.placa}
              className="h-14 w-14 rounded-lg object-cover border shrink-0"
            />
          )}
          <div>
            <h1 className="text-3xl font-bold">{tituloDocumento}</h1>
            <p className="text-muted-foreground">
              {descricaoDocumento} · {veiculo.placa} — {veiculo.marca} {veiculo.modelo} {veiculo.ano}
              {veiculo.quilometragem ? ` · ${veiculo.quilometragem.toLocaleString('pt-BR')} km` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr]">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isConsignacao ? (
                  <ClipboardList className="h-5 w-5" />
                ) : mostrarFinanceiro ? (
                  <DollarSign className="h-5 w-5" />
                ) : (
                  <ClipboardList className="h-5 w-5" />
                )}
                {tituloCardResumo}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoja && (
                <div className="grid grid-cols-2 gap-1 rounded-xl border bg-muted/30 p-1">
                  <Button
                    type="button"
                    variant={modalidadeDocumento === 'compra' ? 'default' : 'ghost'}
                    className="h-9"
                    onClick={() => setModalidadeDocumento('compra')}
                  >
                    Compra
                  </Button>
                  <Button
                    type="button"
                    variant={modalidadeDocumento === 'consignacao' ? 'default' : 'ghost'}
                    className="h-9"
                    onClick={() => setModalidadeDocumento('consignacao')}
                  >
                    Consignação
                  </Button>
                </div>
              )}

              {mostrarFinanceiro && !isConsignacao ? (
                <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Valor FIPE</span>
                      {valorFIPEOrigem && (
                        <Badge variant="secondary" className="shrink-0">
                          {valorFIPEOrigem === 'api' ? 'Automático' : 'Manual'}
                        </Badge>
                      )}
                    </div>
                    <Input
                      aria-label="Valor FIPE"
                      value={carregandoFIPE ? "Carregando..." : valorFIPEInput}
                      onChange={(event) => handleValorFIPEChange(event.target.value)}
                      onBlur={() => setValorFIPEInput(valorFIPE > 0 ? formatarMoedaInput(valorFIPE) : "")}
                      disabled={carregandoFIPE}
                      placeholder="Informe o valor FIPE"
                      className="h-9 bg-background text-right font-semibold"
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Custo de reparos</span>
                    <span className="font-semibold text-destructive">- {formatCurrency(custoTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3 text-sm">
                    <span className="font-medium text-foreground">Valor após reparos</span>
                    <span className="font-semibold text-foreground">{formatCurrency(valorSugerido)}</span>
                  </div>
                  <div className="grid gap-2 border-t pt-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Valor máximo recomendado</span>
                      <span className="font-semibold text-foreground">{formatCurrency(valorSugerido)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Risco comercial</span>
                      <Badge variant="outline" className={riscoCompra.className}>{riscoCompra.label}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Baseado em FIPE, reparos estimados e pontos críticos documentados na avaliação.
                    </p>
                  </div>
                </div>
              ) : isConsignacao ? (
                <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Partes avaliadas</span>
                    <span className="font-semibold text-foreground">{criterios.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Pontos observados</span>
                    <span className="font-semibold text-destructive">{reparos.length}</span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3 text-sm">
                    <span className="font-medium text-foreground">Base documental</span>
                    <span className="font-semibold text-foreground">{fotosGerais.length} fotos</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Partes vistoriadas</span>
                    <span className="font-semibold text-foreground">{criterios.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Com observação</span>
                    <span className="font-semibold text-warning-foreground">{atencaoItems.length}</span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3 text-sm">
                    <span className="font-medium text-foreground">Com dano</span>
                    <span className="font-semibold text-destructive">{reparos.length}</span>
                  </div>
                </div>
              )}

              <div className="space-y-3 border-t pt-3">
                {mostrarFinanceiro && !isConsignacao && (
                  <div className="space-y-2">
                    <Label htmlFor="oferta">Valor do fechamento</Label>
                    <Input
                      id="oferta"
                      placeholder="R$ 0,00"
                      value={oferta}
                      onChange={(e) => setOferta(aplicarMascaraMoeda(e.target.value))}
                      onBlur={() => setOferta(formatarMoedaInput(parsearMoeda(oferta)))}
                      className="text-lg font-semibold text-center"
                    />
                    {porcentagemFipe && (
                      <p className="text-center text-sm text-muted-foreground">
                        {Number(porcentagemFipe) > 0 ? '+' : ''}{porcentagemFipe}% do FIPE
                      </p>
                    )}
                  </div>
                )}

                {isConsignacao && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="valor-anunciado">Valor anunciado</Label>
                      <Input
                        id="valor-anunciado"
                        placeholder="R$ 0,00"
                        value={valorAnunciado}
                        onChange={(event) => setValorAnunciado(aplicarMascaraMoeda(event.target.value))}
                        onBlur={() => setValorAnunciado(formatarMoedaInput(parsearMoeda(valorAnunciado)))}
                        className="text-lg font-semibold text-center"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="comissao-loja">Comissão da loja</Label>
                      <Input
                        id="comissao-loja"
                        placeholder="Ex.: 5% ou R$ 2.000,00"
                        value={comissaoLoja}
                        onChange={(event) => setComissaoLoja(event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="prazo-consignacao">Prazo de consignação</Label>
                      <Input
                        id="prazo-consignacao"
                        placeholder="Ex.: 60 dias"
                        value={prazoConsignacao}
                        onChange={(event) => setPrazoConsignacao(event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="condicoes-consignacao">Observações/condições</Label>
                      <Textarea
                        id="condicoes-consignacao"
                        placeholder="Condições combinadas com o proprietário"
                        value={condicoesConsignacao}
                        onChange={(event) => setCondicoesConsignacao(event.target.value)}
                        className="min-h-[92px]"
                      />
                    </div>
                  </div>
                )}

                {mostrarFinanceiro ? (
                  /* ── LOJA: compartilhar em destaque ── */
                  <div className="grid gap-2">
                    <Button
                      className="w-full border-green-200 bg-green-600 text-white hover:bg-green-700"
                      onClick={handleCompartilharProposta}
                      disabled={compartilhando}
                    >
                      {compartilhando
                        ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                        : <Share2 className="mr-2 h-4 w-4" />
                      }
                      {propostaShareToken ? 'Compartilhar novamente' : 'Compartilhar link'}
                    </Button>
                    <Button variant="outline" className="w-full" onClick={handleGerarPDF} disabled={gerandoPDF}>
                      {gerandoPDF
                        ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                        : <FileDown className="mr-2 h-4 w-4" />
                      }
                      {gerandoPDF ? ctaGerandoDocumento : ctaGerarDocumento}
                    </Button>
                    <div className="flex items-center justify-between pt-1">
                      <Button variant="ghost" size="sm" onClick={salvarProposta} disabled={salvando} className="gap-1.5 text-xs text-muted-foreground">
                        {salvando
                          ? <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-current" />
                          : <Save className="h-3.5 w-3.5" />
                        }
                        {propostaSalva ? 'Salvo' : 'Salvar'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-xs text-muted-foreground"
                        onClick={handleCopiarResumo}
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Resumo
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-xs text-muted-foreground"
                        onClick={() => {
                          if (propostaShareToken) {
                            void copiarLinkProposta(propostaShareToken)
                          } else {
                            toast.info("Gere o link primeiro clicando em Compartilhar link")
                          }
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Link
                      </Button>
                    </div>
                    {propostaSalva && (
                      <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground" asChild>
                        <Link to={`/veiculo/${veiculo?.id}`}>Voltar para veículo</Link>
                      </Button>
                    )}
                  </div>
                ) : (
                  /* ── OFICINA: compartilhar em destaque ── */
                  <div className="grid gap-2">
                    <Button
                      className="w-full border-green-200 bg-green-600 text-white hover:bg-green-700"
                      onClick={handleCompartilhar}
                      disabled={compartilhando}
                    >
                      {compartilhando
                        ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                        : <Share2 className="mr-2 h-4 w-4" />
                      }
                      {shareToken ? 'Compartilhar novamente' : 'Compartilhar via WhatsApp'}
                    </Button>
                    <Button variant="outline" className="w-full" onClick={handleGerarPDF} disabled={gerandoPDF}>
                      {gerandoPDF
                        ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                        : <FileDown className="mr-2 h-4 w-4" />
                      }
                      {gerandoPDF ? vertical.documentos.ctaGerando : vertical.documentos.ctaGerar}
                    </Button>
                    <div className="flex items-center justify-between pt-1">
                      <Button variant="ghost" size="sm" onClick={salvarProposta} disabled={salvando} className="gap-1.5 text-xs text-muted-foreground">
                        {salvando
                          ? <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-current" />
                          : <Save className="h-3.5 w-3.5" />
                        }
                        {propostaSalva ? 'Salvo' : 'Salvar'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-xs text-muted-foreground"
                        onClick={() => {
                          if (shareToken) {
                            navigator.clipboard.writeText(`${window.location.origin}/t/${shareToken}`)
                              .then(() => toast.success("Link copiado"))
                              .catch(() => toast.error("Erro ao copiar link"))
                          } else {
                            toast.info("Gere o link primeiro clicando em Compartilhar")
                          }
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copiar link
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-xs text-muted-foreground"
                        onClick={() => navigate(`/diagnostico/novo?veiculo=${veiculo?.id}&avaliacao=${id}`)}
                      >
                        <Stethoscope className="h-3.5 w-3.5" />
                        Diagnóstico
                      </Button>
                    </div>
                  </div>
                )}

                {propostaId && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full text-destructive hover:bg-transparent hover:text-destructive/90"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Excluir {vertical.documentos.nomeOutput.toLowerCase()}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          {shareToken || propostaShareToken
                            ? `Este documento já foi compartilhado com o cliente. Ao excluir, o link público deixará de funcionar e os dados de ciência serão perdidos. Tem certeza?`
                            : `Tem certeza que deseja excluir o ${vertical.documentos.nomeOutput.toLowerCase()}? Esta ação não pode ser desfeita.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => void handleDeletarProposta()}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Confirmar Exclusão
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <PenLine className="h-5 w-5" />
                Assinatura de ciência
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(
                <div className="grid gap-3 rounded-lg border bg-muted/20 p-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="cliente-termo-nome">
                      {mostrarFinanceiro && isConsignacao ? "Nome do proprietário" : "Nome do cliente"}
                    </Label>
                    <Input
                      id="cliente-termo-nome"
                      value={assinaturaNome}
                      onChange={(event) => setAssinaturaNome(event.target.value)}
                      placeholder={mostrarFinanceiro && isConsignacao ? "Nome do proprietário" : "Nome do cliente ou responsável"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cliente-termo-telefone">WhatsApp/telefone</Label>
                    <Input
                      id="cliente-termo-telefone"
                      value={assinaturaTelefone}
                      onChange={(event) => setAssinaturaTelefone(event.target.value)}
                      placeholder="(00) 00000-0000"
                      inputMode="tel"
                    />
                  </div>
                </div>
              )}

              {avaliacao?.cliente_assinado_em ? (
                /* ── Já assinado: mostra imagem ── */
                <div className="space-y-3">
                  <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                    {assinaturaDataUrl && (
                      <img
                        src={assinaturaDataUrl}
                        alt="Assinatura registrada"
                        className="w-full rounded border bg-white object-contain"
                        style={{ height: 100 }}
                      />
                    )}
                  {assinaturaNome && (
                    <p className="text-sm font-medium text-foreground">{assinaturaNome}</p>
                  )}
                  {assinaturaTelefone && (
                    <p className="text-xs text-muted-foreground">{assinaturaTelefone}</p>
                  )}
                    <p className="text-xs text-muted-foreground">
                      Registrada em {new Date(avaliacao.cliente_assinado_em).toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground"
                    onClick={() => {
                      setAssinaturaNome("")
                      setAssinaturaDataUrl("")
                      setIncluirAssinatura(true)
                    }}
                  >
                    Refazer assinatura
                  </Button>
                </div>
              ) : (
                /* ── Sem assinatura: formulário ── */
                <>
                  <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                    <Checkbox
                      checked={incluirAssinatura}
                      onCheckedChange={(checked) => setIncluirAssinatura(checked === true)}
                      className="mt-0.5"
                    />
                    <span className="space-y-1">
                      <span className="block text-sm font-medium">Incluir assinatura no documento final</span>
                      <span className="block text-xs text-muted-foreground">
                        {isConsignacao
                          ? 'Use para registrar ciência das condições de consignação pelo proprietário.'
                          : 'Use para registrar ciência das condições do veículo pelo proprietário ou cliente.'}
                      </span>
                    </span>
                  </label>

                  {incluirAssinatura && (
                    <div className="space-y-4 border-t pt-4">
                      {mostrarFinanceiro && (
                        <div className="space-y-2">
                          <Label htmlFor="assinatura-nome">Nome completo</Label>
                          <Input
                            id="assinatura-nome"
                            value={assinaturaNome}
                            onChange={(event) => setAssinaturaNome(event.target.value)}
                            placeholder="Nome de quem assina"
                          />
                        </div>
                      )}

                      {mostrarFinanceiro ? (
                        <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="assinatura-documento">CPF/CNPJ</Label>
                          <Input
                            id="assinatura-documento"
                            value={assinaturaDocumento}
                            onChange={(event) => setAssinaturaDocumento(event.target.value)}
                            placeholder="Documento"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="assinatura-telefone">Telefone</Label>
                          <Input
                            id="assinatura-telefone"
                            value={assinaturaTelefone}
                            onChange={(event) => setAssinaturaTelefone(event.target.value)}
                            placeholder="Telefone"
                          />
                        </div>
                        </div>
                      ) : null}

                      <div className="space-y-2">
                        <Label>Assinatura</Label>
                        <SignaturePad value={assinaturaDataUrl} onChange={setAssinaturaDataUrl} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-primary" />
                Registro fotográfico
                <Badge variant="outline" className="ml-2">
                  {fotosGerais.length} {fotosGerais.length === 1 ? 'foto' : 'fotos'}
                </Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {mostrarFinanceiro
                  ? 'Fotos registradas durante a avaliação do veículo.'
                  : 'Estado visual documentado no momento da entrada do veículo.'}
              </p>
            </CardHeader>
            <CardContent>
              {fotosGerais.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {fotosGerais.map((foto, index) => (
                    <Dialog key={index}>
                      <DialogTrigger asChild>
                        <div className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg border bg-muted">
                          <img
                            src={foto.url}
                            alt={LABEL_CATEGORIA_FOTO[foto.categoria] ?? foto.categoria}
                            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                            <p className="text-[11px] font-medium text-white leading-tight">
                              {LABEL_CATEGORIA_FOTO[foto.categoria] ?? foto.categoria}
                            </p>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl flex flex-col items-center gap-3">
                        <p className="text-sm font-medium text-foreground">
                          {LABEL_CATEGORIA_FOTO[foto.categoria] ?? foto.categoria}
                        </p>
                        <img
                          src={foto.url}
                          alt={LABEL_CATEGORIA_FOTO[foto.categoria] ?? foto.categoria}
                          className="rounded-lg border object-contain"
                          style={{ maxWidth: "90vw", maxHeight: "80vh" }}
                        />
                      </DialogContent>
                    </Dialog>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center text-muted-foreground">
                  <Camera className="mx-auto mb-2 h-10 w-10 opacity-25" />
                  <p className="text-sm">
                    {mostrarFinanceiro ? 'Nenhuma foto registrada nesta avaliação' : 'Nenhuma foto registrada nesta entrada'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  {isConsignacao ? "Pontos documentados" : mostrarFinanceiro ? "Reparos necessários" : "Danos registrados"}
                  <Badge variant="outline" className="ml-2 bg-red-50 text-red-700">
                    {reparos.length} {reparos.length === 1 ? 'item' : 'itens'}
                  </Badge>
                </CardTitle>
                {mostrarFinanceiro && !isConsignacao && (
                  <div className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
                    Custo total: {formatCurrency(custoTotal)}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reparos.map((criterio, index) => {
                  const foto = fotosReparos.find(
                    f => f.categoria === `criterio_reprovado_${criterio.criterio_id}`
                  )
                  const nome = obterNome(criterio)
                  const categoria = obterCategoria(criterio)

                  return (
                    <div
                      key={index}
                      className="border rounded-lg flex flex-row items-stretch gap-4 p-2 md:p-3 shadow-sm bg-white"
                      style={{ minHeight: 100 }}
                    >
                      {foto ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <img
                              src={foto.url}
                              alt={nome}
                              className="rounded-lg border cursor-pointer object-cover self-center shrink-0"
                              style={{ width: 90, height: 90 }}
                            />
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl flex flex-col items-center">
                            <img
                              src={foto.url}
                              alt={nome}
                              className="rounded-lg border object-contain"
                              style={{ maxWidth: "90vw", maxHeight: "80vh" }}
                            />
                          </DialogContent>
                        </Dialog>
                      ) : (
                        <div className="flex items-center justify-center w-[90px] h-[90px] bg-muted rounded-lg border text-xs text-muted-foreground opacity-60 shrink-0 self-center">
                          Sem foto
                        </div>
                      )}
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-base">{nome}</p>
                            {categoria && (
                              <p className="text-xs text-muted-foreground mt-0.5">{categoria}</p>
                            )}
                          </div>
                          <Badge variant="destructive" className="ml-2 h-fit shrink-0 px-2 py-1 text-xs">
                            {mostrarFinanceiro && !isConsignacao ? formatCurrency(criterio.custo_reparo || 0) : isConsignacao ? "Avaliação" : "Dano"}
                          </Badge>
                        </div>
                        {criterio.observacoes && (
                          <p className="text-xs text-muted-foreground mt-1">
                            <span className="font-semibold">Obs:</span> {criterio.observacoes}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}

                {reparos.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Check className="h-12 w-12 mx-auto mb-2 text-green-500" />
                    <p>{isConsignacao ? "Nenhum ponto crítico documentado" : mostrarFinanceiro ? "Nenhum reparo necessário" : "Nenhum dano registrado"}</p>
                    <p className="text-sm">{isConsignacao ? "Avaliação anexada sem reparos críticos" : mostrarFinanceiro ? "Todos os critérios foram aprovados" : "Veículo entrou sem danos documentados"}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {atencaoItems.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    {mostrarFinanceiro ? "Pontos de atenção" : "Observações importantes"}
                    <Badge variant="outline" className="ml-2 border-amber-300 bg-amber-50 text-amber-700">
                      {atencaoItems.length} {atencaoItems.length === 1 ? 'item' : 'itens'}
                    </Badge>
                  </CardTitle>
                </div>
                <p className="text-xs text-muted-foreground">
                  {mostrarFinanceiro ? "Itens que merecem acompanhamento mas não foram reprovados." : "Itens marcados para acompanhamento durante o serviço."}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {atencaoItems.map((criterio, index) => {
                    const foto = fotosReparos.find(
                      f => f.categoria === `criterio_atencao_${criterio.criterio_id}`
                    )
                    const nome = obterNome(criterio)
                    const categoria = obterCategoria(criterio)
                    return (
                      <div
                        key={index}
                        className="border rounded-lg flex flex-row items-stretch gap-4 p-2 md:p-3 shadow-sm bg-white"
                        style={{ minHeight: 100 }}
                      >
                        {foto ? (
                          <Dialog>
                            <DialogTrigger asChild>
                              <img
                                src={foto.url}
                                alt={nome}
                                className="rounded-lg border cursor-pointer object-cover self-center shrink-0"
                                style={{ width: 90, height: 90 }}
                              />
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl flex flex-col items-center">
                              <img
                                src={foto.url}
                                alt={nome}
                                className="rounded-lg border object-contain"
                                style={{ maxWidth: "90vw", maxHeight: "80vh" }}
                              />
                            </DialogContent>
                          </Dialog>
                        ) : (
                          <div className="flex items-center justify-center w-[90px] h-[90px] bg-muted rounded-lg border text-xs text-muted-foreground opacity-60 shrink-0 self-center">
                            Sem foto
                          </div>
                        )}
                        <div className="flex-1 flex flex-col justify-between py-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-base">{nome}</p>
                              {categoria && (
                                <p className="text-xs text-muted-foreground mt-0.5">{categoria}</p>
                              )}
                            </div>
                            <Badge variant="outline" className="ml-2 text-xs px-2 py-1 h-fit shrink-0 border-amber-300 bg-amber-50 text-amber-700">
                              Atenção
                            </Badge>
                          </div>
                          {criterio.observacoes && (
                            <p className="text-xs text-muted-foreground mt-1">
                              <span className="font-semibold">Obs:</span> {criterio.observacoes}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {avaliacao?.observacoes_iniciais && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">{mostrarFinanceiro ? "Observações da avaliação" : "Observações do cliente / técnico"}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {avaliacao.observacoes_iniciais}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default PropostaDetalhes
